import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LocationDetailsSection, PersonalDetailsSection } from "../../components/profile/edit/basicInfo";
import { FighterAdditionalSection, FighterBasicSection } from "../../components/profile/edit/fighterInfo";
import FightHistorySection from "../../components/profile/edit/fightHistory";
import { Divider, SectionTitle, TextAreaField, getDateLanguage } from "../../components/profile/edit/formPrimitives";
import SocialMediaSection from "../../components/profile/edit/socialMedia";
import SponsorshipsSection from "../../components/profile/edit/sponsorship";
import YoutubeSection from "../../components/profile/edit/youtubeSection";
import { UpdateProfileUIProps } from "../../types/updateProfileProps";

interface NavItem {
    key: string;
    /** Items without a section id navigate to another settings tab instead of scrolling */
    sectionId?: string;
}

const FIGHTER_NAV_ITEMS: NavItem[] = [
    { key: "profile", sectionId: "section-profile" },
    { key: "personalDetails", sectionId: "section-personal-details" },
    { key: "locationDetails", sectionId: "section-location-details" },
    { key: "fighterBasic", sectionId: "section-fighter-basic" },
    { key: "fighterAdditional", sectionId: "section-fighter-additional" },
    { key: "fightHistory", sectionId: "section-fight-history" },
    { key: "youtubeLinks", sectionId: "section-youtube-links" },
    { key: "sponsors", sectionId: "section-sponsors" },
    { key: "linkAccount" },
    { key: "deleteAccount", sectionId: "section-delete-account" },
];

const OTHER_NAV_ITEMS: NavItem[] = [
    { key: "profile", sectionId: "section-profile" },
    { key: "personalDetails", sectionId: "section-personal-details" },
    { key: "locationDetails", sectionId: "section-location-details" },
    { key: "linkAccount" },
    { key: "deleteAccount", sectionId: "section-delete-account" },
];

const BIO_MAX_LENGTH = 1000;
const SCROLL_OFFSET = 24;
const ACTIVE_LINE_OFFSET = 120;
const BOTTOM_TOLERANCE = 4;

/**
 * The page does not scroll the window: layout.tsx wraps the content in
 * <main className="overflow-y-auto">. Walk up to find the element that really scrolls.
 */
const findScrollParent = (element: HTMLElement): HTMLElement | null => {
    let parent = element.parentElement;

    while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") return parent;
        parent = parent.parentElement;
    }

    return null;
};

const ProfileSettingsTab: React.FC<UpdateProfileUIProps> = (props) => {
    const {
        member,
        form,
        setField,
        errors,
        isFighter,
        imageUrl,
        uploadingImage,
        onPickImage,
        deletion,
    } = props;

    const { t, i18n } = useTranslation("updateProfile");
    const navigate = useNavigate();
    const [, setSearchParams] = useSearchParams();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeKey, setActiveKey] = useState("profile");

    const navItems = isFighter ? FIGHTER_NAV_ITEMS : OTHER_NAV_ITEMS;

    // ── anchor nav: highlight the section that is currently on screen ──
    useEffect(() => {
        const firstSection = document.getElementById("section-profile");
        if (!firstSection) return;

        const scrollContainer = findScrollParent(firstSection);
        let frameId = 0;

        const isAtBottom = () => {
            if (scrollContainer) {
                const scrolled = scrollContainer.scrollTop + scrollContainer.clientHeight;
                return scrolled >= scrollContainer.scrollHeight - BOTTOM_TOLERANCE;
            }
            return window.innerHeight + window.scrollY >= document.body.scrollHeight - BOTTOM_TOLERANCE;
        };

        const updateActiveItem = () => {
            const containerTop = scrollContainer ? scrollContainer.getBoundingClientRect().top : 0;
            let currentKey = navItems[0].key;

            navItems.forEach((item) => {
                if (!item.sectionId) return;
                const element = document.getElementById(item.sectionId);
                if (!element) return;
                if (element.getBoundingClientRect().top <= containerTop + ACTIVE_LINE_OFFSET) {
                    currentKey = item.key;
                }
            });

            // the last section can be too short to ever reach the active line
            if (isAtBottom()) currentKey = navItems[navItems.length - 1].key;

            setActiveKey(currentKey);
        };

        // one update per animation frame is enough while scrolling
        const handleScroll = () => {
            if (frameId) return;
            frameId = window.requestAnimationFrame(() => {
                frameId = 0;
                updateActiveItem();
            });
        };

        updateActiveItem();

        const scrollTarget = scrollContainer ?? window;
        scrollTarget.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            if (frameId) window.cancelAnimationFrame(frameId);
            scrollTarget.removeEventListener("scroll", handleScroll);
        };
    }, [navItems]);

    const handleNavClick = (item: NavItem) => {
        if (!item.sectionId) {
            setSearchParams({ tab: "linkAccount" });
            return;
        }

        const element = document.getElementById(item.sectionId);
        if (!element) return;

        setActiveKey(item.key);

        const scrollContainer = findScrollParent(element);
        if (scrollContainer) {
            const distance = element.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top;
            scrollContainer.scrollTo({
                top: scrollContainer.scrollTop + distance - SCROLL_OFFSET,
                behavior: "smooth",
            });
            return;
        }

        window.scrollTo({
            top: window.scrollY + element.getBoundingClientRect().top - SCROLL_OFFSET,
            behavior: "smooth",
        });
    };

    // ── avatar ──
    const handleImageSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // reset, so choosing the same file again still fires onChange
        event.target.value = "";
        if (file) onPickImage(file);
    };

    const nickname = member.fighter?.nickname ?? "";
    const fullName = `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();
    const initials = `${member.firstName?.charAt(0) ?? ""}${member.lastName?.charAt(0) ?? ""}`.toUpperCase();

    const formatDeletionDate = (isoDate: string) => {
        const date = new Date(isoDate);
        if (Number.isNaN(date.getTime())) return isoDate;
        return date.toLocaleDateString(getDateLanguage(i18n.language), { day: "numeric", month: "long", year: "numeric" });
    };

    return (
        <div className="flex w-full items-start gap-6">
            <div className="flex min-w-0 flex-1 flex-col gap-6">
                {/* ── Profile ── */}
                <section id="section-profile" className="flex flex-col gap-4">
                    <p className="text-sm font-medium leading-5 text-gray-900">{t("page.profile")}</p>

                    <div className="flex items-center gap-4">
                        <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full bg-[#EBEBEC]">
                            {imageUrl ? (
                                <img src={imageUrl} alt={fullName} className="h-full w-full object-cover" />
                            ) : (
                                <span className="flex h-full w-full items-center justify-center text-2xl font-bold text-gray-500">
                                    {initials}
                                </span>
                            )}
                            {uploadingImage && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#0485F7] border-t-transparent" />
                                </div>
                            )}
                        </div>

                        <div className="flex min-w-0 flex-col">
                            {nickname ? (
                                <>
                                    <p className="break-words text-4xl font-bold leading-10 text-gray-900">
                                        {nickname}
                                    </p>
                                    <p className="break-words text-2xl leading-8 text-gray-900">{fullName}</p>
                                </>
                            ) : (
                                <p className="break-words text-4xl font-bold leading-10 text-gray-900">{fullName}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelected}
                            className="hidden"
                        />
                        <button
                            type="button"
                            disabled={uploadingImage}
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex h-8 items-center rounded-full bg-[#EBEBEC] px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-300 disabled:opacity-50"
                        >
                            {t("page.editImage")}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(`/profile?profileId=${member.id}`)}
                            className="inline-flex h-8 items-center rounded-full bg-[#EBEBEC] px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-300"
                        >
                            {t("page.viewProfile")}
                        </button>
                    </div>

                    <TextAreaField
                        name="bio"
                        label={t("page.bio")}
                        maxLength={BIO_MAX_LENGTH}
                        value={form.bio}
                        placeholder={t("page.writeHere")}
                        counterText={t("page.characters", { count: form.bio.length, max: BIO_MAX_LENGTH })}
                        onValueChange={(value) => setField("bio", value)}
                        error={errors.bio}
                    />

                    <SocialMediaSection {...props} />
                </section>

                <Divider />
                <PersonalDetailsSection {...props} />

                <Divider />
                <LocationDetailsSection {...props} />

                {isFighter && (
                    <>
                        <Divider />
                        <FighterBasicSection {...props} />

                        <Divider />
                        <FighterAdditionalSection {...props} />

                        <Divider />
                        <FightHistorySection {...props} />

                        <Divider />
                        <YoutubeSection {...props} />

                        <Divider />
                        <SponsorshipsSection {...props} />
                    </>
                )}

                <Divider />

                {/* ── Delete account ── */}
                <section id="section-delete-account" className="flex flex-col gap-4">
                    <SectionTitle>{t("page.deleteAccount")}</SectionTitle>

                    {deletion.scheduledAt ? (
                        <>
                            <p className="text-base leading-6 text-gray-900">
                                {t("page.deleteScheduledOn", { date: formatDeletionDate(deletion.scheduledAt) })}
                            </p>
                            <button
                                type="button"
                                disabled={deletion.busy}
                                onClick={deletion.cancel}
                                className="inline-flex h-8 w-fit items-center rounded-full bg-[#EBEBEC] px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-300 disabled:opacity-50"
                            >
                                {t("page.cancelDeletion")}
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="text-base leading-6 text-gray-900">{t("page.deleteText")}</p>
                            <button
                                type="button"
                                disabled={deletion.busy}
                                onClick={deletion.request}
                                className="inline-flex h-8 w-fit items-center rounded-full bg-red-50 px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
                            >
                                {t("page.deleteAccount")}
                            </button>
                        </>
                    )}
                </section>
            </div>

            {/* ── Anchor nav (desktop only) ── */}
            <aside className="sticky top-6 hidden w-60 shrink-0 flex-col gap-4 self-start lg:flex">
                {navItems.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => handleNavClick(item)}
                        className={`w-fit text-left text-sm transition-colors ${
                            activeKey === item.key
                                ? "font-semibold text-[#1553B6]"
                                : "font-medium text-[#0485F7] hover:text-[#1553B6]"
                        }`}
                    >
                        {t(`page.nav.${item.key}`)}
                    </button>
                ))}
            </aside>
        </div>
    );
};

export default ProfileSettingsTab;
